import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import api from '../services/api';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function MapView() {
  const [markers, setMarkers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMapData();
    const interval = setInterval(loadMapData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadMapData = async () => {
    try {
      const [markersRes, statsRes] = await Promise.all([
        api.get('/map/agents'),
        api.get('/map/stats')
      ]);
      setMarkers(markersRes.data.markers);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load map data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMarkerColor = (status) => {
    return status === 'online' ? '#00ff88' : '#ff3366';
  };

  const getOsIcon = (osType) => {
    switch (osType?.toLowerCase()) {
      case 'windows': return '🪟';
      case 'linux': return '🐧';
      case 'darwin':
      case 'macos': return '🍎';
      default: return '💻';
    }
  };

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Global Agent Map</h1>
        <div className="header-actions">
          <span style={{ color: 'var(--text-muted)' }}>
            {stats?.total_agents || 0} agents in {stats?.total_countries || 0} countries
          </span>
        </div>
      </header>

      <div className="main-body" style={{ height: 'calc(100vh - 140px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 'var(--spacing-lg)', height: '100%' }}>
          {/* Map */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="card-body" style={{ padding: 0, height: '100%' }}>
              {loading ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  background: 'var(--bg-primary)'
                }}>
                  <div className="loader"></div>
                </div>
              ) : (
                <MapContainer
                  center={[20, 0]}
                  zoom={2}
                  style={{ height: '100%', width: '100%', background: '#0a0a0f' }}
                  zoomControl={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  />
                  {markers.map((marker, index) => (
                    marker.latitude && marker.longitude && (
                      <CircleMarker
                        key={marker.agent_id || index}
                        center={[marker.latitude, marker.longitude]}
                        radius={8}
                        pathOptions={{
                          color: getMarkerColor(marker.status),
                          fillColor: getMarkerColor(marker.status),
                          fillOpacity: 0.7,
                          weight: 2
                        }}
                      >
                        <Popup>
                          <div style={{ 
                            fontFamily: 'var(--font-mono)', 
                            fontSize: '12px',
                            minWidth: '200px'
                          }}>
                            <div style={{ 
                              fontWeight: 'bold', 
                              marginBottom: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span>{getOsIcon(marker.os_type)}</span>
                              {marker.hostname || 'Unknown Host'}
                            </div>
                            <div style={{ marginBottom: '4px' }}>
                              <strong>IP:</strong> {marker.external_ip}
                            </div>
                            <div style={{ marginBottom: '4px' }}>
                              <strong>Location:</strong> {marker.city}, {marker.country}
                            </div>
                            <div style={{ marginBottom: '4px' }}>
                              <strong>Status:</strong>{' '}
                              <span style={{ color: getMarkerColor(marker.status) }}>
                                {marker.status}
                              </span>
                            </div>
                            <div style={{ marginBottom: '4px' }}>
                              <strong>OS:</strong> {marker.os_type}
                            </div>
                            <div>
                              <strong>Last Seen:</strong>{' '}
                              {marker.last_seen && new Date(marker.last_seen).toLocaleString()}
                            </div>
                          </div>
                        </Popup>
                      </CircleMarker>
                    )
                  ))}
                </MapContainer>
              )}
            </div>
          </div>

          {/* Stats Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
            {/* Legend */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Legend</h3>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%', 
                    background: '#00ff88',
                    boxShadow: '0 0 8px #00ff88'
                  }}></div>
                  <span>Online Agent</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%', 
                    background: '#ff3366'
                  }}></div>
                  <span>Offline Agent</span>
                </div>
              </div>
            </div>

            {/* Country Stats */}
            <div className="card" style={{ flex: 1, overflow: 'hidden' }}>
              <div className="card-header">
                <h3 className="card-title">By Country</h3>
              </div>
              <div className="card-body" style={{ overflow: 'auto', maxHeight: '400px' }}>
                {stats?.countries?.map((country, index) => (
                  <div 
                    key={country.country_code}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: index < stats.countries.length - 1 ? '1px solid var(--border-color)' : 'none'
                    }}
                  >
                    <span>{country.country}</span>
                    <span style={{ 
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: 'var(--neon-green)' }}>{country.online}</span>
                      <span style={{ color: 'var(--text-muted)' }}>/</span>
                      <span>{country.total}</span>
                    </span>
                  </div>
                ))}
                {(!stats?.countries || stats.countries.length === 0) && (
                  <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
                    No location data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MapView;
